import React, { useMemo, useState } from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import {
  activatePropertiesWallet,
  createWalletProperty
} from '../../services/properties';
import { unmaskMoney } from '../../services/masks';
import { successNotification } from '../../services/notification';
import Button from '../../components/Button';
import FilterBar, { FilterField, FilterValues } from '../../components/Filter';

import {
  buildInitialFilters,
  orderByDirOptions,
  orderByOptions,
  walletPropertyTypeOptions
} from './constants';
import { usePropertiesWalletList } from './hooks/usePropertiesWalletList';
import PropertiesTable from './components/PropertiesTable';
import CreatePropertyModal, {
  CreatePropertyFormValues
} from './components/CreatePropertyModal';
import CreateCondominiumModal, {
  CreateCondominiumFormValues
} from './components/CreateCondominiumModal';
import PropertyDetailsModal from './components/PropertyDetailsModal';
import { PropertyWalletItemDTO } from '../../services/properties';
import { FiltersState } from './types';
import { isBackofficeTestUser } from '../../util';
import {
  ActivationError,
  ActivationMessage,
  ActivationText,
  ContentSegment,
  PageActions,
  PageContainer
} from './styles';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const toSelectOptions = (
  items: Array<{ value: string; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const PropertiesWalletPage = ({ availableBrokersAndEstates, user }: Props) => {
  const history = useHistory();
  const role = user?.role as UserRolesEnum | undefined;
  const isEstateUser = (role || '').includes('ESTATE');
  const isBackofficeTest = isBackofficeTestUser(user);
  const defaultEstateId = useMemo(() => {
    return availableBrokersAndEstates?.activeEstateId || user?.estate?.id || '';
  }, [availableBrokersAndEstates?.activeEstateId, user?.estate?.id]);

  const initialFilters = useMemo(
    () => buildInitialFilters(isEstateUser ? defaultEstateId : defaultEstateId),
    [defaultEstateId, isEstateUser]
  );

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map(
      (item: CustomDropDownOptions) => {
        const valueId = typeof item.value === 'string' ? item.value : item.value?.id;
        return {
          key: valueId || String(item.key),
          text: item.text || '',
          value: valueId || ''
        };
      }
    );
  }, [availableBrokersAndEstates?.estates]);

  const baseListEnabled = isEstateUser || Boolean(defaultEstateId);

  const {
    isLoading,
    items,
    total,
    estateContext,
    currentPage,
    setCurrentPage,
    appliedFilters,
    listEnabled,
    errorMessage,
    isScopeError,
    loadData,
    applyFilters,
    clearFilters
  } = usePropertiesWalletList({
    initialFilters,
    enabled: baseListEnabled
  });

  const [isActivatingWallet, setIsActivatingWallet] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState<'property' | 'condominium'>('property');
  const [isCreatingProperty, setIsCreatingProperty] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<PropertyWalletItemDTO | null>(
    null
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const hasActiveFilters = Boolean(appliedFilters.search || appliedFilters.type);
  const shouldShowActivateButton =
    listEnabled &&
    !isLoading &&
    !errorMessage &&
    Boolean(estateContext && !estateContext.rvUuid);
  const canImportProperties = listEnabled && !shouldShowActivateButton;
  const selectedEstateId = appliedFilters.estateId || defaultEstateId || undefined;

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'text',
        name: 'search',
        label: 'Buscar',
        placeholder: 'Endereço, código ou apelido',
        width: 1.4
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        width: 1.2
      });
    }

    fields.push(
      {
        kind: 'select',
        name: 'type',
        label: 'Tipo do imóvel',
        placeholder: 'Todos',
        options: toSelectOptions(walletPropertyTypeOptions),
        width: 1
      },
      {
        kind: 'select',
        name: 'orderBy',
        label: 'Ordenar por',
        placeholder: 'Ordenar por',
        options: toSelectOptions(orderByOptions),
        clearable: false,
        width: 1
      },
      {
        kind: 'select',
        name: 'orderByDir',
        label: 'Direção',
        placeholder: 'Direção',
        options: toSelectOptions(orderByDirOptions),
        clearable: false,
        width: 0.8
      }
    );

    return fields;
  }, [isEstateUser, estateOptions]);

  const normalizeFilters = (values: FilterValues): FiltersState => ({
    estateId: String(values.estateId || (isEstateUser ? defaultEstateId : '') || ''),
    search: String(values.search || ''),
    type: String(values.type || ''),
    orderBy: String(values.orderBy || 'updated_at'),
    orderByDir: values.orderByDir === 'ASC' ? 'ASC' : 'DESC'
  });

  const handleFilterSearch = (values: FilterValues) => {
    applyFilters(normalizeFilters(values));
  };

  const handleActivateWallet = async () => {
    try {
      setActivationError(null);
      setIsActivatingWallet(true);
      await activatePropertiesWallet(appliedFilters.estateId || defaultEstateId);
      setCurrentPage(1);
      await loadData(1, appliedFilters);
    } catch (error) {
      setActivationError(
        error instanceof Error
          ? error.message
          : 'Não foi possível ativar a carteira agora. Tente novamente.'
      );
    } finally {
      setIsActivatingWallet(false);
    }
  };

  const getCreateErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'object' && error !== null) {
      const message = (error as { message?: string | string[] }).message;
      if (Array.isArray(message)) return message.join(', ');
      if (typeof message === 'string' && message.trim()) return message;
    }
    return 'Não foi possível cadastrar o imóvel agora. Tente novamente.';
  };

  const handleCreateProperty = async (values: CreatePropertyFormValues) => {
    if (isBackofficeTest) {
      return;
    }

    try {
      setCreateError(null);
      setIsCreatingProperty(true);

      const areaValue = values.area.trim().replace(',', '.');
      const listFilters = {
        ...appliedFilters,
        estateId: isEstateUser
          ? appliedFilters.estateId || defaultEstateId
          : values.estateId
      };

      await createWalletProperty({
        estateId: isEstateUser ? undefined : values.estateId,
        registration: values.registration.trim() || undefined,
        propertyType: values.propertyType,
        area: areaValue ? Number(areaValue) : undefined,
        furniture: values.furniture || undefined,
        suggestedRentalValue: values.suggestedRentalValue
          ? unmaskMoney(values.suggestedRentalValue)
          : undefined,
        suggestedSaleValue: values.suggestedSaleValue
          ? unmaskMoney(values.suggestedSaleValue)
          : undefined,
        address: {
          zipcode: values.zipcode.trim(),
          street: values.street.trim(),
          neighborhood: values.neighborhood.trim(),
          number: values.number.trim(),
          complement: values.complement.trim() || undefined,
          city: values.city.trim(),
          state: values.state
        }
      });

      setIsCreateModalOpen(false);
      successNotification('Sucesso', 'Imóvel cadastrado com sucesso.');
      applyFilters(listFilters);
    } catch (error) {
      setCreateError(getCreateErrorMessage(error));
      throw error;
    } finally {
      setIsCreatingProperty(false);
    }
  };

  const handleCreateCondominium = async (values: CreateCondominiumFormValues) => {
    if (isBackofficeTest) {
      return;
    }

    try {
      setCreateError(null);
      setIsCreatingProperty(true);

      const listFilters = {
        ...appliedFilters,
        estateId: isEstateUser
          ? appliedFilters.estateId || defaultEstateId
          : values.estateId
      };
      const condominiumDocument = values.condominium.document.replace(/[^0-9]/g, '');
      const syndicDocument = values.condominium.syndic.document.replace(/[^0-9]/g, '');

      await createWalletProperty({
        estateId: isEstateUser ? undefined : values.estateId,
        identification: values.condominium.legalName.trim(),
        registration: condominiumDocument || undefined,
        propertyType: 'CONDOMINIO',
        area: 0,
        address: {
          zipcode: values.zipcode.replace(/[^0-9]/g, ''),
          street: values.street.trim(),
          neighborhood: values.neighborhood.trim(),
          number: values.number.trim(),
          complement: values.complement.trim() || undefined,
          city: values.city.trim(),
          state: values.state
        },
        condominium: {
          document: condominiumDocument,
          legalName: values.condominium.legalName.trim(),
          type: values.condominium.type,
          units: values.condominium.units.trim()
            ? Number(values.condominium.units)
            : undefined,
          assessor: values.condominium.assessor.trim() || undefined,
          phone: values.condominium.phone.trim() || undefined,
          email: values.condominium.email.trim() || undefined,
          syndic: {
            name: values.condominium.syndic.name.trim(),
            document: syndicDocument || undefined,
            rg: values.condominium.syndic.rg.trim() || undefined,
            birthDate: values.condominium.syndic.birthDate || undefined,
            email: values.condominium.syndic.email.trim(),
            phone: values.condominium.syndic.phone.trim()
          }
        }
      });

      setIsCreateModalOpen(false);
      successNotification('Sucesso', 'Condomínio cadastrado com sucesso.');
      applyFilters(listFilters);
    } catch (error) {
      setCreateError(getCreateErrorMessage(error));
      throw error;
    } finally {
      setIsCreatingProperty(false);
    }
  };

  const openCreateModal = (mode: 'property' | 'condominium') => {
    setCreateMode(mode);
    setCreateError(null);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (isCreatingProperty) return;
    setIsCreateModalOpen(false);
    setCreateError(null);
  };

  const handleOpenPropertyDetails = (item: PropertyWalletItemDTO) => {
    setSelectedProperty(item);
    setIsDetailsOpen(true);
  };

  const handleClosePropertyDetails = () => {
    setIsDetailsOpen(false);
    setSelectedProperty(null);
  };

  return (
    <PageContainer>
      <PageActions>
        <Dropdown
          trigger={
            <Button icon type="button">
              Criar novo <Icon name="dropdown" />
            </Button>
          }
          icon={null}
          disabled={isBackofficeTest}
        >
          <Dropdown.Menu>
            <Dropdown.Item text="Imóvel" onClick={() => openCreateModal('property')} />
            <Dropdown.Item
              text="Condomínio"
              onClick={() => openCreateModal('condominium')}
            />
          </Dropdown.Menu>
        </Dropdown>
      </PageActions>

      <FilterBar
        fields={filterFields}
        initialValues={initialFilters}
        onSearch={handleFilterSearch}
        onClear={clearFilters}
        loading={isLoading}
        syncToUrl
        urlNamespace="wallet"
        actions={
          canImportProperties ? (
            <Button
              light
              type="button"
              text="Importar imóveis"
              onClick={() => history.push('/admin/properties/import')}
              style={{ height: 38 }}
            />
          ) : null
        }
      />

      <ContentSegment>
        {shouldShowActivateButton && (
          <ActivationMessage info>
            <ActivationText>
              Ative a carteira de imóveis para iniciar a sincronização dos imóveis da sua
              imobiliária.
            </ActivationText>
            <Button
              type="button"
              loading={isActivatingWallet}
              disabled={isActivatingWallet}
              onClick={handleActivateWallet}
              text="Ativar carteira"
            />
            {activationError && <ActivationError>{activationError}</ActivationError>}
          </ActivationMessage>
        )}

        {!shouldShowActivateButton && (
          <PropertiesTable
            isLoading={isLoading}
            items={items}
            currentPage={currentPage}
            total={total}
            setCurrentPage={setCurrentPage}
            errorMessage={errorMessage}
            isScopeError={isScopeError}
            hasActiveFilters={hasActiveFilters}
            listEnabled={listEnabled}
            onOpenDetails={handleOpenPropertyDetails}
          />
        )}
      </ContentSegment>

      <PropertyDetailsModal
        open={isDetailsOpen}
        property={selectedProperty}
        estateId={selectedEstateId}
        onClose={handleClosePropertyDetails}
      />

      {createMode === 'condominium' ? (
        <CreateCondominiumModal
          open={isCreateModalOpen}
          userId={user?.id}
          showEstateField={!isEstateUser}
          estateOptions={estateOptions}
          initialEstateId={selectedEstateId}
          isSubmitting={isCreatingProperty}
          submitError={createError}
          isBackofficeTest={isBackofficeTest}
          onClose={closeCreateModal}
          onSubmit={handleCreateCondominium}
        />
      ) : (
        <CreatePropertyModal
          open={isCreateModalOpen}
          userId={user?.id}
          showEstateField={!isEstateUser}
          estateOptions={estateOptions}
          initialEstateId={selectedEstateId}
          isSubmitting={isCreatingProperty}
          submitError={createError}
          isBackofficeTest={isBackofficeTest}
          onClose={closeCreateModal}
          onSubmit={handleCreateProperty}
        />
      )}
    </PageContainer>
  );
};

export default connect(mapState, null)(PropertiesWalletPage);
